import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { t } from '../i18n/index.js';
import { fitFocused } from '../platform/keyboardFit.js';
import { captureTrigger, returnFocus } from '../platform/focusReturn.js';

/** Liegt das Ziel in einem anderen (darüber geöffneten) Fenster? */
const otherDialog = (e: Event) => {
  const target = ((e as CustomEvent<{ originalEvent?: Event }>).detail?.originalEvent?.target ?? e.target) as HTMLElement | null;
  return !!target?.closest?.('[role="dialog"], [role="alertdialog"]');
};

/** Seitliches Fenster (Desktop rechts, am Handy bildschirmfüllend) mit Fokusführung und Esc */
export function Sheet({
  open,
  onClose,
  title,
  children,
  wide,
  navigation,
  under,
  header,
  tinted,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  wide?: boolean | 'workspace';
  navigation?: ReactNode;
  /**
   * Abnahme 0.58 F01: ein anderes Fenster liegt darüber (Aufgabe über dem Datei-Arbeitsplatz). Dann bleibt dieses
   * offen, aber darunter – ohne Fokusfalle, Esc oder Klick daneben schließen nur das obere.
   */
  under?: boolean;
  /**
   * Briefing „Seitenleisten“ (ADR 0175): fester Kopf des Inhalts (Titel, Speicherstatus, Menü) statt der kleinen
   * Fensterzeile – bleibt beim Scrollen und Reiterwechsel stehen. `title` bleibt als zugänglicher Fenstername.
   */
  header?: ReactNode;
  /** leicht getönte Inhaltsfläche, auf der die Abschnitte als Container stehen */
  tinted?: boolean;
}) {
  // F05: Auslöser beim Öffnen merken – Schließen gibt den Fokus dorthin zurück (auch nach Schließbewegung)
  const trigger = useRef<ReturnType<typeof captureTrigger>>(null);
  const openedAt = useRef(0);
  const panel = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (!open) return;
    trigger.current = captureTrigger();
    openedAt.current = performance.now();
  }, [open]);
  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && !under && onClose()} modal={!under}>
      <Dialog.Portal>
        {!under && <Dialog.Overlay className="sheet-overlay fixed inset-0 z-40 bg-black/20 dark:bg-black/50" />}
        <Dialog.Content
          ref={panel}
          aria-describedby={undefined}
          // Hinweise (z. B. „Rückgängig“) liegen außerhalb – ein Klick darauf schließt das Fenster nicht
          onInteractOutside={(e) => {
            const original = (e as CustomEvent<{ originalEvent?: Event & { werftToast?: boolean } }>).detail?.originalEvent;
            if (original?.werftToast) e.preventDefault();
            // F05: Klick während der Schließbewegung, der dieses Fenster neu öffnet (nächste Aufgabe) – Radix wertet
            // „daneben“ erst nach dem Klick aus; ein Ereignis von vor dem Öffnen schließt nicht wieder
            if (original && original.timeStamp < openedAt.current) e.preventDefault();
            // Abnahme 0.58 F01: ein darüber geöffnetes Fenster (Aufgabe über Datei/Notiz) schließt das untere nicht
            if (otherDialog(e) || under) e.preventDefault();
          }}
          onFocusOutside={(e) => {
            if (otherDialog(e) || under) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (!under) return;
            // Liegt dieses Fenster unten, gilt Esc dem oberen (Radix gibt Esc an das zuletzt geöffnete weiter)
            e.preventDefault();
            document.querySelector<HTMLElement>('[role="dialog"]:not([inert]) [data-sheet-close]')?.click();
          }}
          onOpenAutoFocus={(e) => {
            if (under) e.preventDefault();
          }}
          onCloseAutoFocus={(e) => {
            // Hat die Ansicht den Fokus während der Schließbewegung schon gesetzt (z. B. auf die zuletzt gezeigte
            // Datei), bleibt er dort; sonst zurück an den Auslöser
            const active = document.activeElement as HTMLElement | null;
            const placed = !!active && active !== document.body && !panel.current?.contains(active);
            if (placed || returnFocus(trigger.current)) e.preventDefault();
          }}
          {...(under ? { 'aria-hidden': true, inert: true } : {})}
          // F07: bei Bildschirmtastatur bleibt das fokussierte Feld sichtbar
          onFocusCapture={(e) => fitFocused(e.target)}
          className={`sheet-panel fixed inset-y-0 right-0 ${under ? 'z-30' : 'z-40'} flex w-full flex-col bg-white shadow-xl outline-none dark:bg-zinc-950 ${navigation ? 'sm:max-w-[41rem]' : wide === 'workspace' ? 'sm:max-w-xl lg:max-w-[min(76rem,calc(100vw-4rem))]' : wide ? 'sm:max-w-xl' : 'sm:max-w-md'}`}
        >
          <div className={`flex gap-2 border-b border-zinc-200 dark:border-zinc-800 ${header ? 'items-start px-2 py-1 sm:px-4' : 'items-center px-4 py-2'}`}>
            <Dialog.Title className={header ? 'sr-only' : 'min-w-0 flex-1 truncate text-sm font-medium text-zinc-500'}>{title}</Dialog.Title>
            {header && <div className="min-w-0 flex-1">{header}</div>}
            <Dialog.Close data-sheet-close="" className="grid size-11 place-items-center rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800" aria-label={t('common.close')}>
              <X className="size-5" aria-hidden />
            </Dialog.Close>
          </div>
          {navigation ? <div className="flex min-h-0 flex-1 flex-col sm:flex-row">
            <div className="order-2 shrink-0 border-t border-zinc-200 bg-zinc-50 pb-[env(safe-area-inset-bottom)] sm:order-1 sm:w-20 sm:border-t-0 sm:border-r sm:py-3 dark:border-zinc-800 dark:bg-zinc-900">{navigation}</div>
            <div className={`order-1 min-h-0 min-w-0 flex-1 overflow-y-auto p-4 sm:order-2 ${tinted ? 'bg-zinc-50 dark:bg-zinc-900' : ''}`} data-sheet-scroll="">{children}</div>
          </div> : <div className={`min-h-0 flex-1 overflow-y-auto p-4 ${tinted ? 'bg-zinc-50 dark:bg-zinc-900' : ''}`} data-sheet-scroll="">{children}</div>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
